import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField, IconButton, Tooltip } from '@mui/material';
import { Send as SendIcon, AttachFile as AttachIcon, Stop as StopIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { colors } from '../../utils/designSystem';

interface MessageInputProps {
  onSendMessage: (message: string) => void;
  onStop?: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  placeholder?: string;
}

const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  onStop,
  disabled = false,
  isLoading = false,
  placeholder = 'Ask about hotel valuation, market analysis, or financial calculations...',
}) => {
  const [message, setMessage] = useState('');
  const textFieldRef = useRef<HTMLDivElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    const textField = textFieldRef.current;
    if (textField) {
      const textarea = textField.querySelector('textarea');
      if (textarea) {
        textarea.style.height = 'auto';
        const scrollHeight = textarea.scrollHeight;
        textarea.style.height = `${Math.min(scrollHeight, 200)}px`;
      }
    }
  }, [message]);

  const handleSendMessage = () => {
    const trimmedMessage = message.trim();
    if (trimmedMessage && !disabled && !isLoading) {
      onSendMessage(trimmedMessage);
      setMessage('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value);
  };

  const canSend = message.trim() && !disabled && !isLoading;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 1.5,
        p: 1.5,
        bgcolor: colors.gray[50],
        borderRadius: 3,
        border: '1px solid',
        borderColor: colors.gray[200],
        transition: 'all 0.2s ease',
        '&:focus-within': {
          borderColor: colors.primary[500],
          bgcolor: 'white',
          boxShadow: `0 0 0 3px ${colors.primary[500]}1a`,
        },
      }}
    >
      {/* Attach button (placeholder) */}
      <Tooltip title="Attach file (coming soon)">
        <span>
          <IconButton
            size="small"
            disabled
            sx={{
              width: 36,
              height: 36,
              color: colors.gray[400],
              '&:hover': { bgcolor: colors.gray[100] },
            }}
          >
            <AttachIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </span>
      </Tooltip>

      {/* Text input */}
      <TextField
        ref={textFieldRef}
        fullWidth
        multiline
        maxRows={5}
        value={message}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        variant="standard"
        InputProps={{
          disableUnderline: true,
          sx: {
            fontSize: '0.9375rem',
            lineHeight: 1.6,
            color: colors.gray[900],
            '& textarea': {
              resize: 'none',
              '&::placeholder': {
                color: colors.gray[400],
                opacity: 1,
              },
            },
          },
        }}
        inputProps={{
          'aria-label': 'Message input',
        }}
      />

      {/* Send / Stop button */}
      {isLoading && onStop ? (
        <motion.div whileTap={{ scale: 0.92 }}>
          <Tooltip title="Stop generating">
            <IconButton
              onClick={onStop}
              size="small"
              sx={{
                width: 40,
                height: 40,
                bgcolor: '#ef4444',
                color: 'white',
                borderRadius: 2,
                '&:hover': {
                  bgcolor: '#dc2626',
                },
                transition: 'all 0.15s ease',
              }}
              aria-label="Stop generating"
            >
              <StopIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </motion.div>
      ) : (
        <motion.div whileTap={canSend ? { scale: 0.92 } : {}}>
          <IconButton
            onClick={handleSendMessage}
            disabled={!canSend}
            size="small"
            sx={{
              width: 40,
              height: 40,
              bgcolor: canSend ? colors.gray[900] : colors.gray[200],
              color: canSend ? 'white' : colors.gray[400],
              borderRadius: 2,
              '&:hover': {
                bgcolor: canSend ? colors.gray[800] : colors.gray[200],
              },
              '&:disabled': {
                bgcolor: colors.gray[200],
                color: colors.gray[400],
              },
              transition: 'all 0.15s ease',
            }}
            aria-label="Send message"
          >
            <SendIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </motion.div>
      )}
    </Box>
  );
};

export default MessageInput;
